import React, { useState, useEffect } from "react";
import "./App.css";
import "./i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import LiveChat from "./components/LiveChat";
import Homepage from "./pages/Homepage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmation from "./pages/OrderConfirmation";
import MyAccount from "./pages/MyAccount";
import Login from "./pages/Login";
import Register from "./pages/Register";
import WishlistPage from "./pages/WishlistPage";
import OrderTracking from "./pages/OrderTracking";
import Invoice from "./pages/Invoice";
import VINLookupPage from "./pages/VINLookupPage";
import VehicleSelectorPage from "./pages/VehicleSelectorPage";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./contexts/AuthContext";
import { WishlistProvider } from "./contexts/WishlistContext";

// Admin imports
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./components/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminVehicles from "./pages/admin/AdminVehicles";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminBrands from "./pages/admin/AdminBrands";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminVehicleDatabase from "./pages/admin/AdminVehicleDatabase";
import ProductForm from "./pages/admin/ProductForm";
import EditProduct from "./pages/admin/EditProduct";

// Protected Admin Route
const ProtectedAdminRoute = ({ children }) => {
  const adminToken = localStorage.getItem('admin_token');
  
  if (!adminToken) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  const [cart, setCart] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('autodoc-cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('autodoc-cart', JSON.stringify(cart));
  }, [cart]);

  const handleAddToCart = (product) => {
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(
        item => item.product.id === product.id
      );

      if (existingItemIndex > -1) {
        const newCart = [...prevCart];
        newCart[existingItemIndex].quantity += 1;
        return newCart;
      } else {
        return [...prevCart, { product, quantity: 1 }];
      }
    });
  };

  const handleUpdateQuantity = (index, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(index);
      return;
    }
    
    setCart(prevCart => {
      const newCart = [...prevCart];
      newCart[index].quantity = newQuantity;
      return newCart;
    });
  };

  const handleRemoveFromCart = (index) => {
    setCart(prevCart => prevCart.filter((_, i) => i !== index));
  };

  const handleClearCart = () => {
    setCart([]);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <AuthProvider>
      <WishlistProvider>
        <div className="App">
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <>
                <Navbar cartCount={cartCount} />
                <Homepage onAddToCart={handleAddToCart} />
                <Footer />
              </>
            } />
            <Route path="/products" element={
              <>
                <Navbar cartCount={cartCount} />
                <ProductsPage onAddToCart={handleAddToCart} />
                <Footer />
              </>
            } />
            <Route path="/product/:id" element={
              <>
                <Navbar cartCount={cartCount} />
                <ProductDetailPage onAddToCart={handleAddToCart} />
                <Footer />
              </>
            } />
            <Route path="/cart" element={
              <>
                <Navbar cartCount={cartCount} />
                <CartPage 
                  cart={cart} 
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemoveFromCart={handleRemoveFromCart}
                />
                <Footer />
              </>
            } />
            <Route path="/checkout" element={
              <>
                <Navbar cartCount={cartCount} />
                <CheckoutPage cart={cart} onClearCart={handleClearCart} />
                <Footer />
              </>
            } />
            <Route path="/order-confirmation/:orderNumber" element={
              <>
                <Navbar cartCount={cartCount} />
                <OrderConfirmation />
                <Footer />
              </>
            } />
            <Route path="/my-account" element={
              <>
                <Navbar cartCount={cartCount} />
                <MyAccount />
                <Footer />
              </>
            } />
            <Route path="/login" element={
              <>
                <Navbar cartCount={cartCount} />
                <Login />
                <Footer />
              </>
            } />
            <Route path="/register" element={
              <>
                <Navbar cartCount={cartCount} />
                <Register />
                <Footer />
              </>
            } />
            <Route path="/wishlist" element={
              <>
                <Navbar cartCount={cartCount} />
                <WishlistPage onAddToCart={handleAddToCart} />
                <Footer />
              </>
            } />
            <Route path="/vin-lookup" element={
              <>
                <Navbar cartCount={cartCount} />
                <VINLookupPage />
                <Footer />
              </>
            } />
            <Route path="/vehicle-selector" element={
              <>
                <Navbar cartCount={cartCount} />
                <VehicleSelectorPage />
                <Footer />
              </>
            } />
            <Route path="/track/:orderNumber" element={
              <>
                <Navbar cartCount={cartCount} />
                <OrderTracking />
                <Footer />
              </>
            } />
            <Route path="/invoice/:orderNumber" element={
              <>
                <Invoice />
              </>
            } />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedAdminRoute>
                <AdminLayout />
              </ProtectedAdminRoute>
            }>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="products/create" element={<ProductForm mode="create" />} />
              <Route path="products/edit/:id" element={<EditProduct />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="vehicles" element={<AdminVehicles />} />
              <Route path="coupons" element={<AdminCoupons />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="brands" element={<AdminBrands />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="vehicle-database" element={<AdminVehicleDatabase />} />
            </Route>
          </Routes>
          <Toaster />
          <LiveChat />
        </BrowserRouter>
      </div>
    </WishlistProvider>
  </AuthProvider>
  );
}

export default App;
